import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuth } from '../hooks/useAuth'
import type { Permission } from '../types'

interface ProtectedRouteProps {
    permissions?: Permission[]
    requireAll?: boolean
    requireSuperAdmin?: boolean
    children?: React.ReactNode
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    permissions = [],
    requireAll = false,
    requireSuperAdmin = false,
    children
}) => {
    const { isAuthenticated, isLoading, hasAllPermissions, hasAnyPermission, isSuperAdmin } = useAuth()
    const location = useLocation()

    // Show loading spinner while checking authentication
    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh'
            }}>
                <Spin size="large" tip="Loading..." />
            </div>
        )
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />
    }

    // Check if Super Admin is explicitly required
    if (requireSuperAdmin && !isSuperAdmin) {
        return <Navigate to="/403" replace />
    }

    // Check permissions if specified
    if (permissions.length > 0) {
        const hasRequiredPermissions = requireAll
            ? hasAllPermissions(permissions)
            : hasAnyPermission(permissions)

        if (!hasRequiredPermissions) {
            return <Navigate to="/403" replace />
        }
    }

    return children ? <>{children}</> : <Outlet />
}
